import React, { useState, useEffect, useCallback } from 'react';
import { CircleUserRound, Bookmark, Heart, Trash2, Edit2, Calendar, Users, MapPin, Check, Star, MessageCircle } from 'lucide-react';
import Masonry from 'react-masonry-css';
import { getAuth } from 'firebase/auth';
import './UserProfile.css';
import Spinner from '../../components/spinner/Spinner'
import ImagePopup from '../explore/components/ImagePopup';
import { generateToken } from '../../services/shared/generateToken';
import { UserProfileAPI } from '../../services/api/userProfileAPI';
import { FavoritesAPI } from '../../services/api/favoritesApi.ts';
import { Vendor } from '../../types/vendor.ts';
import { useNavigate, useLocation } from 'react-router-dom';
import StoryCard from '../connect/components/StoryCard.tsx';
import MessagesSection from '../connect/components/MessagesSection.tsx';

/**
 * UserProfile Component
 * Displays user's profile information, generated images, and liked images
 * Supports image management (viewing, deleting) and infinite scrolling
 */

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('generated');
  const [userData, setUserData] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreImages, setHasMoreImages] = useState(true);
  const [likedImages, setLikedImages] = useState([]);
  const [likedImagesPage, setLikedImagesPage] = useState(0);
  const [hasMoreLikedImages, setHasMoreLikedImages] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [favoriteVendors, setFavoriteVendors] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const breakpointColumnsObj = {
    default: 3,
    1100: 2,
    700: 1
  };

  /**
   * Initial user profile data fetch
   * Runs once on component mount
   * 1. Gets current user from Firebase Auth
   * 2. Fetches user document from Firestore
   * 3. Updates userData state or shows error
   */
  useEffect(() => {
    const fetchUserProfile = async () => {
      const auth = getAuth();
      
      if (auth.currentUser && auth.currentUser.email) {
        try {
          console.log("Current user email:", auth.currentUser.email); // Debug log
          const response = await UserProfileAPI.getUserProfile(auth.currentUser.email);
          
          if (response) {
            setUserData(response);
            setEditedData({
              person1FirstName: response.person1FirstName || '',
              person1LastName: response.person1LastName || '',
              person2FirstName: response.person2FirstName || '',
              person2LastName: response.person2LastName || '',
              weddingDate: response.weddingDate || '',
              guestCount: response.guestCount || 0,
              weddingLocation: response.weddingLocation || '',
              emailAddress: auth.currentUser.email, // Use Firebase email directly
              id: response.id,
              venderType: response.venderType || '',
              companyName: response.companyName || '',
              phoneNumber: response.phoneNumber || '',
              location: response.location || '',
              wedify_user_type: response.wedify_user_type || '',
              authenticationService: response.authenticationService || '',
              externalClientId: auth.currentUser.uid,
              authType: response.authType || ''
            });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setError(error.message);
        } finally {
          setIsInitialLoading(false);
        }
      } else {
        console.error("No authenticated user or email found");
        setError("No authenticated user or email found");
        setIsInitialLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Set initial tab based on URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'favorites') {
      setActiveTab('favorites');
    }
  }, [location]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const auth = getAuth();
    
    try {
      const updatedProfile = await UserProfileAPI.updateUserProfile(
        auth.currentUser.uid,
        {
          person1FirstName: editedData.person1FirstName,
          person1LastName: editedData.person1LastName,
          person2FirstName: editedData.person2FirstName,
          person2LastName: editedData.person2LastName,
          weddingDate: editedData.weddingDate,
          guestCount: editedData.guestCount,
          weddingLocation: editedData.weddingLocation
        }
      );
      setUserData(updatedProfile);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Fetches user's generated images
   * Supports pagination and infinite scroll
   * 1. Generates authentication token
   * 2. Fetches images for current page
   * 3. Updates bookmarks state, either replacing or appending based on page
   * 4. Updates hasMoreImages flag for infinite scroll
   */
  const fetchUserData = useCallback(async () => {
    setIsContentLoading(true);
    setError(null);
    const auth = getAuth();
    
    try {
 
      const response = await UserProfileAPI.getUserImages(currentPage)
    

      if (Array.isArray(response)) {
        setBookmarks(prevBookmarks => currentPage === 0 ? response : [...prevBookmarks, ...response]);
        setHasMoreImages(response.length > 0);
      } else {
        console.error("Received data structure is unexpected:", response);
        setBookmarks([]);
        setHasMoreImages(false);
      }
    } catch (error) {
      console.error("Error fetching bookmarks:", error);
      setError(error.message);
    } finally {
      setIsContentLoading(false);
    }
  }, [currentPage]);

  /**
   * Fetches user's liked images
   * Similar to fetchUserData but for liked images
   * Supports pagination and infinite scroll
   */
  const fetchLikedImages = useCallback(async () => {
    setIsContentLoading(true);
    setError(null);
    const auth = getAuth();
    
    try {
      const token = await generateToken();
      if (!token) {
        throw new Error('Failed to generate authentication token');
      }

      const response = await UserProfileAPI.getLikedImages(token, {
        externalClientId: auth.currentUser.uid,
        page: likedImagesPage
      });

      if (Array.isArray(response)) {
        setLikedImages(prevLikedImages => likedImagesPage === 0 ? response : [...prevLikedImages, ...response]);
        setHasMoreLikedImages(response.length > 0);
      } else {
        console.error("Received data structure is unexpected:", response);
        setLikedImages([]);
        setHasMoreLikedImages(false);
      }
    } catch (error) {
      console.error("Error fetching liked images:", error);
      setError(error.message);
    } finally {
      setIsContentLoading(false);
    }
  }, [likedImagesPage]);

  /**
   * Handles deletion of user-generated images
   * 1. Shows confirmation dialog
   * 2. Generates auth token
   * 3. Calls API to delete image
   * 4. Updates local state on success
   * @param {string} imageId - ID of image to delete
   */
  const handleDeleteGeneratedImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const token = await generateToken();
      if (!token) {
        throw new Error('Failed to generate authentication token');
      }

      const status = await UserProfileAPI.deleteGeneratedImage(token, imageId);

      if (status === 200) {
        setBookmarks(prevBookmarks => prevBookmarks.filter(bookmark => bookmark.id !== imageId));
      } else {
        throw new Error(`HTTP error! status: ${status}`);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(`Failed to delete the image. Error: ${error.message}`);
    }
  };

  /**
   * Handles removal of liked images
   * Similar to handleDeleteGeneratedImage but for liked images
   * @param {string} imageId - ID of image to unlike
   */
  const handleDeleteLikedImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to remove this liked image?')) {
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      const token = await generateToken();
      if (!token) {
        throw new Error('Failed to generate authentication token');
      }

      const response = await UserProfileAPI.deleteLikedImage(token, {
        externalClientId: user.uid,
        imageId
      });

      if (response.status === 200) {
        setLikedImages(prevLikedImages => prevLikedImages.filter(image => image.id !== imageId));
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error removing liked image:', error);
    }
  };

  /**
   * Extracts hashtags from image metadata
   * 1. Parses selectedOptions (handles both string and object formats)
   * 2. Filters for relevant metadata keys
   * 3. Formats values as hashtags
   * @param {object|string} selectedOptions - Image metadata
   * @returns {string[]} Array of formatted hashtags
   */
  const extractHashtags = (selectedOptions) => {
    if (!selectedOptions) {
      return [];
    }

    let parsedOptions;
    if (typeof selectedOptions === 'string') {
      try {
        parsedOptions = JSON.parse(selectedOptions);
      } catch (error) {
        console.error('Error parsing selectedOptions:', error);
        return [];
      }
    } else {
      parsedOptions = selectedOptions;
    }

    // For Wedding Attire, we want to directly use the selections
    if (parsedOptions.attireType) {
      const attireSelections = Object.entries(parsedOptions)
        .filter(([key]) => key !== 'metadataValuesList') // Exclude metadataValuesList if present
        .map(([key, value]) => {
          if (key === 'color') {
            const colorValue = typeof value === 'object' ? value.name : value;
            return `#${colorValue.replace(/\s+/g, '')}`;
          }
          const cleanValue = String(value).replace(/[/\s]+/g, '');
          return `#${cleanValue}`;
        });
      return attireSelections;
    }

    let metadataValuesList = parsedOptions.metadataValuesList;
    
    if (!Array.isArray(metadataValuesList)) {
      return [];
    }
    
    const relevantKeys = [
        'theme', 
        'color', 
        'flowers', 
        'seating', 
        'venue',
        'greenery',
        'size',
        'shape',
        'flavor',
        'decoration',
        'fit', 
        'lapel', 
        'material', 
        'pattern', 
        'jacket',
        'style', 
        'neckline', 
        'fabric', 
        'length', 
        'details',
        'location', 
        'scene', 
        'decor', 
        'style'
    ];
    return metadataValuesList
      .filter(option => relevantKeys.includes(option.key))
      .map(option => {
        const capitalizedValue = option.value
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('');
        const cleanedValue = option.key === 'seating' ? capitalizedValue.replace(/\(|\)/g, '') : capitalizedValue;
        return `#${cleanedValue.replace(/\s+/g, '')}`;
      });
  };

  /**
   * Renders individual image card with overlay
   * Includes hashtags, creation date, and delete button
   * @param {object} image - Image data to render
   * @param {boolean} isLiked - Whether image is from liked section
   * @returns {JSX.Element} Image card component
   */
  const renderBookmarkImage = (image, isLiked = false) => {
    const hashtags = extractHashtags(image.selectedOptions);
    
    return (
      <div key={image.id} className="grid-item">
        {image.s3Url ? (
          <img 
            src={image.s3Url} 
            alt={`Image ${image.id}`}
            onClick={() => setSelectedImage(image)}
            onError={(e) => {
              console.error(`Failed to load image: ${image.s3Url}`);
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="image-placeholder">Image not available</div>
        )}
        <div className="item-overlay">
          <div className="item-details">
            <div className="hashtags">
              {hashtags.length > 0 ? (
                hashtags.map((tag, index) => (
                  <span key={index} className="hashtag">{tag}</span>
                ))
              ) : (
                <span className="hashtag">#NoTagsAvailable</span>
              )}
            </div>
            <p>Created: {new Date(image.createdAt).toLocaleDateString()}</p>
          </div>
          <button 
            className="delete-button" 
            onClick={(e) => {
              e.stopPropagation();
              isLiked ? handleDeleteLikedImage(image.id) : handleDeleteGeneratedImage(image.id);
            }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  /**
   * Pagination handlers
   * Increment page counters for infinite scroll
   */
  const loadMoreImages = () => {
    setCurrentPage(prevPage => prevPage + 1);
  };

  const loadMoreLikedImages = () => {
    setLikedImagesPage(prevPage => prevPage + 1);
  };

  /**
   * Effect to fetch appropriate data when tab changes
   * Calls either fetchUserData or fetchLikedImages based on activeTab
   */
  useEffect(() => {
    if (activeTab === 'generated') {
      fetchUserData();
    } else if (activeTab === 'likes') {
      fetchLikedImages();
    }
  }, [fetchUserData, fetchLikedImages, activeTab]);

  // Fetch favorite vendors
  const fetchFavoriteVendors = async () => {
    try {
      setLoadingFavorites(true);
      const favorites = await FavoritesAPI.getFavoriteVendors();
      console.log('Fetched favorite vendors:', favorites); // Debug log
      setFavoriteVendors(favorites);
    } catch (error) {
      console.error('Error fetching favorite vendors:', error);
      setError(error.message);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (vendor) => {
    try {
      if (favoriteVendors.some(v => v.id === vendor.id)) {
        await FavoritesAPI.removeVendorFromFavorites(String(vendor.id));
        setFavoriteVendors(prev => prev.filter(v => v.id !== vendor.id));
      } else {
        await FavoritesAPI.addVendorToFavorites(String(vendor.id));
        setFavoriteVendors(prev => [...prev, vendor]);
      }
    } catch (error) {
      console.error('Failed to update favorite status:', error);
      setError(error.message);
    }
  };

  // Effect to fetch favorite vendors when tab changes
  useEffect(() => {
    if (activeTab === 'favorites') {
      fetchFavoriteVendors();
    }
  }, [activeTab]);

  // Handle vendor selection
  const handleSelectVendor = (vendor) => {
    const vendorNameForUrl = vendor.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    navigate(`/connect/vendor/${vendor.id}/${vendorNameForUrl}`);
  };

  if (isInitialLoading) {
    return <Spinner text="Loading Profile..." />;
  }

  if (error && !userData) {
    return <div className="error">Error: {error}</div>;
  }

  if (!userData) {
    return <div className="not-found">User not found</div>;
  }

  /**
   * Main render
   * Displays:
   * 1. User profile header with user info
   * 2. Tab navigation between generated and liked images
   * 3. Masonry grid of images with infinite scroll
   * 4. Image popup for detailed view
   */
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-rose-50 p-4 md:p-8 relative">
      <div className="max-w-6xl mx-auto pt-24">
        <div className="backdrop-blur-lg bg-gradient-to-br from-white/90 to-white/70 rounded-2xl p-6 shadow-lg mb-8 border border-pink-100">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm flex items-center justify-center border border-pink-100">
                <CircleUserRound size={48} className="text-rose-400" />
              </div>
            </div>
            
            <div className="flex-grow">
              <div className="flex justify-between items-start mb-4">
                <h1 className="text-2xl font-bold text-gray-800">
                  {userData.person1FirstName} & {userData.person2FirstName}
                </h1>
                {isEditing ? (
                  <button
                    onClick={handleSave}
                    className="p-2 rounded-full bg-gradient-to-br from-rose-400/90 to-rose-300/70 hover:from-rose-400 hover:to-rose-300 transition-colors border border-pink-100"
                  >
                    <Check size={20} className="text-white" />
                  </button>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="p-2 rounded-full bg-gradient-to-br from-white/90 to-white/70 hover:from-white hover:to-white transition-colors border border-pink-100"
                  >
                    <Edit2 size={20} className="text-rose-400" />
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="person1FirstName"
                      value={editedData.person1FirstName}
                      onChange={handleInputChange}
                      className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg px-4 py-2 text-gray-800 placeholder-gray-400 border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                      placeholder="First Person's First Name"
                    />
                    <input
                      type="text"
                      name="person1LastName"
                      value={editedData.person1LastName}
                      onChange={handleInputChange}
                      className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg px-4 py-2 text-gray-800 placeholder-gray-400 border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                      placeholder="First Person's Last Name"
                    />
                    <input
                      type="text"
                      name="person2FirstName"
                      value={editedData.person2FirstName}
                      onChange={handleInputChange}
                      className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg px-4 py-2 text-gray-800 placeholder-gray-400 border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                      placeholder="Second Person's First Name"
                    />
                    <input
                      type="text"
                      name="person2LastName"
                      value={editedData.person2LastName}
                      onChange={handleInputChange}
                      className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg px-4 py-2 text-gray-800 placeholder-gray-400 border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                      placeholder="Second Person's Last Name"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-rose-300" size={20} />
                      <input
                        type="date"
                        name="weddingDate"
                        value={editedData.weddingDate?.split('T')[0]}
                        onChange={handleInputChange}
                        className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg pl-10 pr-4 py-2 text-gray-800 w-full border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                      />
                    </div>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-rose-300" size={20} />
                      <input
                        type="number"
                        name="guestCount"
                        value={editedData.guestCount}
                        onChange={handleInputChange}
                        className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg pl-10 pr-4 py-2 text-gray-800 w-full border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                        placeholder="Guest Count"
                      />
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-rose-300" size={20} />
                      <input
                        type="text"
                        name="weddingLocation"
                        value={editedData.weddingLocation}
                        onChange={handleInputChange}
                        className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-lg pl-10 pr-4 py-2 text-gray-800 w-full border border-pink-100 focus:border-rose-200 focus:ring-1 focus:ring-rose-100"
                        placeholder="Wedding Location"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar size={20} className="text-rose-400" />
                      <span>{new Date(userData.weddingDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={20} className="text-rose-400" />
                      <span>{userData.guestCount} Guests</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={20} className="text-rose-400" />
                      <span>{userData.weddingLocation}</span>
                    </div>
                  </div>
                  <p className="text-gray-600">{userData.bio}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="backdrop-blur-lg bg-gradient-to-br from-white/90 to-white/70 rounded-2xl p-6 shadow-lg border border-pink-100">
          <div className="flex flex-wrap gap-2 sm:gap-4 mb-6">
            <button
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm ${
                activeTab === 'generated' 
                  ? 'bg-gradient-to-br from-white to-white/90 text-gray-800 border border-pink-100' 
                  : 'text-gray-600 hover:bg-gradient-to-br hover:from-white/50 hover:to-white/30 border border-transparent'
              }`}
              onClick={() => setActiveTab('generated')}
            >
              <Bookmark size={16} className={activeTab === 'generated' ? 'text-rose-400' : 'text-rose-300'} />
              <span className="hidden sm:inline">Created By You</span>
              <span className="sm:hidden">Created</span>
            </button>
            <button
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm ${
                activeTab === 'likes' 
                  ? 'bg-gradient-to-br from-white to-white/90 text-gray-800 border border-pink-100' 
                  : 'text-gray-600 hover:bg-gradient-to-br hover:from-white/50 hover:to-white/30 border border-transparent'
              }`}
              onClick={() => setActiveTab('likes')}
            >
              <Heart size={16} className={activeTab === 'likes' ? 'text-rose-400' : 'text-rose-300'} />
              <span className="hidden sm:inline">Likes</span>
              <span className="sm:hidden">Liked</span>
            </button>
            <button
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm ${
                activeTab === 'favorites' 
                  ? 'bg-gradient-to-br from-white to-white/90 text-gray-800 border border-pink-100' 
                  : 'text-gray-600 hover:bg-gradient-to-br hover:from-white/50 hover:to-white/30 border border-transparent'
              }`}
              onClick={() => setActiveTab('favorites')}
            >
              <Star size={16} className={activeTab === 'favorites' ? 'text-rose-400' : 'text-rose-300'} />
              <span className="hidden sm:inline">Favorite Vendors</span>
              <span className="sm:hidden">Favorites</span>
            </button>
            <button
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm ${
                activeTab === 'messages' 
                  ? 'bg-gradient-to-br from-white to-white/90 text-gray-800 border border-pink-100' 
                  : 'text-gray-600 hover:bg-gradient-to-br hover:from-white/50 hover:to-white/30 border border-transparent'
              }`}
              onClick={() => setActiveTab('messages')}
            >
              <MessageCircle size={16} className={activeTab === 'messages' ? 'text-rose-400' : 'text-rose-300'} />
              <span className="hidden sm:inline">Inquiries</span>
              <span className="sm:hidden">Inquiries</span>
            </button>
          </div>

          {isContentLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner text="Loading content..." />
            </div>
          ) : (
            <>
              {activeTab === 'generated' && (
                <>
                  <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="masonry-grid"
                    columnClassName="masonry-grid_column"
                  >
                    {bookmarks.map((bookmark) => renderBookmarkImage(bookmark, false))}
                  </Masonry>
                  {hasMoreImages && (
                    <button 
                      className="mt-6 w-full py-3 bg-gradient-to-br from-white/90 to-white/70 hover:from-white hover:to-white text-gray-800 rounded-lg transition-colors border border-pink-100"
                      onClick={loadMoreImages}
                    >
                      Load More
                    </button>
                  )}
                </>
              )}

              {activeTab === 'likes' && (
                <>
                  <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="masonry-grid"
                    columnClassName="masonry-grid_column"
                  >
                    {likedImages.map((image) => renderBookmarkImage(image, true))}
                  </Masonry>
                  {hasMoreLikedImages && (
                    <button 
                      className="mt-6 w-full py-3 bg-gradient-to-br from-white/90 to-white/70 hover:from-white hover:to-white text-gray-800 rounded-lg transition-colors border border-pink-100"
                      onClick={loadMoreLikedImages}
                    >
                      Load More Liked Images
                    </button>
                  )}
                </>
              )}

              {activeTab === 'favorites' && (
                <>
                  {loadingFavorites ? (
                    <div className="flex justify-center items-center h-64">
                      <Spinner text="Loading favorites..." />
                    </div>
                  ) : favoriteVendors.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {favoriteVendors.map((vendor) => (
                        <div key={vendor.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                          <StoryCard 
                            vendor={vendor} 
                            index={0} 
                            featured={false}
                            onSelect={() => handleSelectVendor(vendor)}
                            onFavoriteToggle={() => handleFavoriteToggle(vendor)}
                            isFavorite={true}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 bg-white/60 backdrop-blur-md rounded-xl border border-white/40">
                      <Heart className="w-12 h-12 text-gray-300 mb-4" />
                      <p className="text-[#5D5FEF]/80 text-center text-lg">No favorite vendors yet. Start adding vendors to your favorites!</p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'messages' && (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                  <MessagesSection 
                    onMessageClick={(message) => {
                      console.log('Message clicked:', message);
                      // Handle message click - you can implement your own logic here
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showPopup && selectedImage && (
        <ImagePopup
          image={selectedImage}
          onClose={() => {
            setShowPopup(false);
            setSelectedImage(null);
          }}
        />
      )}
    </div>
  );
};

export default UserProfile;
